<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { FormKitNode } from '@formkit/core'
import { createInput } from '@formkit/vue'
import SelectScheleton from './SelectScheleton.vue'

const customSelect = createInput(SelectScheleton)

export interface Option {
  label: string
  value?: string | number
}

const props = defineProps<{
  name: string
  required: boolean
  label?: string
  modelValue?: string | number
  validation?: string
  validationMessages?: {
    [key: string]: string
  }
  options?: Option[]
  id?: string
}>()
const emit = defineEmits(['update:modelValue', 'change'])

const attrs = useAttrs()
const hasValidation = ref(!!(props.required || props.validation))
const isFocused = ref(false)
const isFilled = ref(!!props.modelValue)

const handleFocus = (f: boolean) => {
  isFocused.value = f
}

const handleInput = (value: unknown) => {
  const typedValue = value as string | number | undefined
  isFilled.value = !!typedValue
  emit('update:modelValue', typedValue)
  emit('change', typedValue)
}

const validationString = computed(() => {
  const validationString = props.validation ?? ''
  const v = validationString.split('|')

  if (props.required) {
    v.push('required')
  }

  return v.filter(Boolean).join('|')
})

const node = ref<FormKitNode | undefined>()
const setNode = (n: FormKitNode) => {
  if (n.value !== '') {
    isFilled.value = true
  }
  return (node.value = n)
}

watch(
  () => props.modelValue,
  (value) => {
    if (!node.value) return
    if (!props.options?.length) return
    node.value.input(value)
  },
)

const classes: Ref<string> = ref('')
watch(
  [isFilled, isFocused, hasValidation, () => attrs.class],
  () => {
    classes.value = [
      isFilled.value ? 'is-filled' : '',
      isFocused.value ? 'is-focused' : '',
      hasValidation.value ? 'has-validation' : '',
      attrs.class ?? '',
    ]
      .filter(Boolean)
      .join(' ')
  },
  { immediate: true },
)
</script>

<template>
<FormKitLazyProvider config-file="true">

  <FormKit
    :id="id"
    :type="customSelect"
    :label="label ?? ''"
    :classes="{
      outer: `custom-select-input ${classes}`,
    }"
    :validation="validationString ?? ''"
    :validation-messages="validationMessages ?? {}"
    :name="name"
    for=""
    :value="modelValue ?? ''"
    :select-options="options ?? []"
    :required="required"
    :handle-focus-fn="(f: boolean) => handleFocus(f)"
    @input="handleInput"
    @node="setNode"
  />

</FormKitLazyProvider>
</template>

<style lang="scss" scoped></style>
